/**
 * Toaster — renders the toast queue (bottom-right), theme-aware, animated.
 * Mounted once in MainLayout. Each toast auto-dismisses after its duration.
 */

import { useEffect }               from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react'

import { useToastStore } from '@store/toastStore'

const TYPE_CONFIG = {
  success: { icon: CheckCircle2, color: 'var(--ac-success-fg)', bg: 'var(--ac-success-bg)', border: 'var(--ac-success)' },
  error:   { icon: AlertCircle,  color: 'var(--ac-danger-fg)',  bg: 'var(--ac-danger-bg)',  border: 'var(--ac-danger)'  },
  info:    { icon: Info,         color: 'var(--ac-info-fg)',    bg: 'var(--ac-info-bg)',    border: 'var(--ac-info)'    },
}

function ToastItem({ toast, onDismiss }) {
  const cfg = TYPE_CONFIG[toast.type] ?? TYPE_CONFIG.info
  const Icon = cfg.icon

  useEffect(() => {
    const t = setTimeout(() => onDismiss(toast.id), toast.duration ?? 3200)
    return () => clearTimeout(t)
  }, [toast.id, toast.duration, onDismiss])

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 40, scale: 0.95 }}
      animate={{ opacity: 1, x: 0,  scale: 1 }}
      exit={{    opacity: 0, x: 40, scale: 0.95 }}
      transition={{ duration: 0.22, ease: [0.34, 1.56, 0.64, 1] }}
      className="flex items-start gap-3 w-80 max-w-[calc(100vw-2rem)] px-4 py-3 rounded-2xl border"
      style={{
        background:     'var(--ac-bg-surface)',
        borderColor:    cfg.border,
        boxShadow:      'var(--ac-shadow-lg)',
        backdropFilter: 'blur(8px)',
      }}
      role="status"
    >
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: cfg.bg }}>
        <Icon size={15} strokeWidth={2} style={{ color: cfg.color }} />
      </div>
      <p className="flex-1 text-sm leading-snug pt-1" style={{ color: 'var(--ac-text-base)' }}>{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss"
        className="flex-shrink-0 mt-0.5 hover:opacity-70 transition-opacity"
        style={{ color: 'var(--ac-text-subtle)' }}
      >
        <X size={14} strokeWidth={2} />
      </button>
    </motion.div>
  )
}

export function Toaster() {
  const toasts  = useToastStore((s) => s.toasts)
  const dismiss = useToastStore((s) => s.dismiss)

  return (
    <div className="fixed bottom-4 right-4 z-[500] flex flex-col gap-2.5 pointer-events-none">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto">
            <ToastItem toast={t} onDismiss={dismiss} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  )
}
